import { useQuery } from '@apollo/client';
import { User } from 'mission-control-common-components';
import { useMemo } from 'react';

import type { DropdownOptionGroup } from '../../../../../common/Dropdown/Dropdown';
import { GET_TRAFFICKING_CUSTOM_FILTERS } from '../queries/traffickingCustomFilters';
import type {
  TraffickingCustomFiltersQueryResponse,
  TraffickingCustomFiltersQueryVariables,
  TraffickingCustomFilterSummary,
} from '../types';
import { CustomFilterType } from '../types';

type UseTraffickingCustomFiltersResult = {
  filters: TraffickingCustomFilterSummary[];
  groups: DropdownOptionGroup<TraffickingCustomFilterSummary>[];
  loading: boolean;
  error: Error | undefined;
};

export function useTraffickingCustomFilters(searchString: string): UseTraffickingCustomFiltersResult {
  const { data, loading, error } = useQuery<
    TraffickingCustomFiltersQueryResponse,
    TraffickingCustomFiltersQueryVariables
  >(GET_TRAFFICKING_CUSTOM_FILTERS, {
    variables: {
      input: {
        pageScope: 'TRAFFICKING',
        searchString: searchString || null,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const filters = useMemo((): TraffickingCustomFilterSummary[] => {
    const edges = data?.traffickingCustomFilters?.edges ?? [];
    const currentUser = User.getUserInfo().userInfo?.email ?? '';

    return edges.map(({ node }) => ({
      id: node.id,
      name: node.filterName,
      visibility: node.visibility as TraffickingCustomFilterSummary['visibility'],
      type: node.filterType as TraffickingCustomFilterSummary['type'],
      createdByMe: (node.createdBy ?? '').toLowerCase() === currentUser.toLowerCase(),
    }));
  }, [data]);

  const groups = useMemo((): DropdownOptionGroup<TraffickingCustomFilterSummary>[] => {
    const globalFilters: TraffickingCustomFilterSummary[] = [];
    const publicFilters: TraffickingCustomFilterSummary[] = [];
    const privateFilters: TraffickingCustomFilterSummary[] = [];

    for (const filter of filters) {
      if (filter.type === CustomFilterType.GLOBAL) {
        globalFilters.push(filter);
      } else if (filter.visibility === 'PUBLIC') {
        publicFilters.push(filter);
      } else {
        privateFilters.push(filter);
      }
    }

    const result: DropdownOptionGroup<TraffickingCustomFilterSummary>[] = [];

    if (globalFilters.length > 0) {
      result.push({
        label: 'Global',
        options: globalFilters.map((f) => ({ id: f.id, label: f.name, value: f })),
      });
    }

    if (privateFilters.length > 0) {
      result.push({
        label: 'Private',
        options: privateFilters.map((f) => ({ id: f.id, label: f.name, value: f })),
      });
    }

    if (publicFilters.length > 0) {
      result.push({
        label: 'Public',
        options: publicFilters.map((f) => ({ id: f.id, label: f.name, value: f })),
      });
    }

    return result;
  }, [filters]);

  return { filters, groups, loading, error };
}
